import gsap from 'gsap'
import SplitText from 'gsap/SplitText'

gsap.registerPlugin(SplitText)

const DEFAULTS = {
  yPercent: 120, // line start position, as % of its height, below the mask (>100% clears descenders)
  duration: 1,
  stagger: 0.1,
  ease: 'power4.out',
  delay: 0,
  linesClass: 'line-reveal-line',
}

/**
 * Split one or more elements into lines, wrap each line in its own clipping
 * mask, and reveal them sliding up from underneath (staggered).
 *
 * The split is created once web fonts are ready (so lines wrap against the real
 * font, not the fallback) and rebuilt on resize, preserving whether the copy is
 * currently shown. Respects `prefers-reduced-motion`: the text is left exactly
 * as authored, with no masks or movement.
 *
 * @param {string|Element|Element[]|NodeList} targets - elements (or a selector) to split
 * @param {object} [options] - overrides: { yPercent, duration, stagger, ease, delay, linesClass }
 * @returns {{ play: (overrides?: object) => (gsap.core.Tween|null), hide: () => void, revert: () => void, lines: Element[] }}
 *
 * @example
 *   const reveal = createLineReveal('.section-title')
 *   reveal.play()                  // reveal the lines
 *   reveal.play({ delay: 0.3 })    // ...or with a one-off delay
 */
export function createLineReveal(targets, options = {}) {
  const config = { ...DEFAULTS, ...options }
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
  const elements = gsap.utils.toArray(targets)

  let splits = []
  let built = false
  let shown = false
  let pendingPlay = null // overrides for a play() requested before the split was ready

  const lines = () => splits.flatMap((split) => split.lines)

  const split = () => {
    splits.forEach((instance) => instance.revert())
    splits = elements.map((el) =>
      SplitText.create(el, { type: 'lines', mask: 'lines', linesClass: config.linesClass })
    )
  }

  // Put the lines wherever `shown` says, without animating — used after a
  // (re)split so the steady state survives resizes and font loading.
  const applyState = () => {
    gsap.set(elements, { autoAlpha: shown ? 1 : 0 })
    if (shown) gsap.set(lines(), { yPercent: 0 })
  }

  const reveal = (overrides) => {
    shown = true
    const vars = { ...config, ...overrides }
    gsap.set(elements, { autoAlpha: 1 })
    return gsap.fromTo(
      lines(),
      { yPercent: vars.yPercent },
      { yPercent: 0, duration: vars.duration, stagger: vars.stagger, ease: vars.ease, delay: vars.delay }
    )
  }

  let resizeTimer
  const onResize = () => {
    if (!built) return
    clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      split()
      applyState()
    }, 200)
  }

  if (reduceMotion) {
    gsap.set(elements, { autoAlpha: 1 })
  } else {
    // Hide until the masks exist so the copy never flashes un-split.
    gsap.set(elements, { autoAlpha: 0 })

    const ready = () => {
      split()
      built = true
      if (pendingPlay) {
        reveal(pendingPlay)
        pendingPlay = null
      } else {
        applyState()
      }
    }
    if (document.fonts) document.fonts.ready.then(ready)
    else ready()

    window.addEventListener('resize', onResize)
  }

  return {
    /** Reveal the lines, each sliding up from behind its mask. Returns the tween (null if not yet ready / reduced motion). */
    play(overrides = {}) {
      if (reduceMotion) {
        gsap.set(elements, { autoAlpha: 1 })
        return null
      }
      if (!built) {
        // The split isn't ready yet (waiting on fonts) — run as soon as it is.
        shown = true
        pendingPlay = overrides
        return null
      }
      return reveal(overrides)
    },

    /** Snap the lines back out of view (below their masks), no animation. */
    hide() {
      shown = false
      pendingPlay = null
      if (reduceMotion) return
      gsap.set(elements, { autoAlpha: 0 })
      if (built) gsap.set(lines(), { yPercent: config.yPercent })
    },

    /** Tear down: restore the original markup and drop listeners. */
    revert() {
      window.removeEventListener('resize', onResize)
      clearTimeout(resizeTimer)
      splits.forEach((instance) => instance.revert())
      splits = []
      built = false
    },

    get lines() {
      return lines()
    },
  }
}
